import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  FiArrowRight,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiArrowLeft,
  FiCheckCircle,
  FiPhone,
  FiPackage,
  FiShoppingCart,
} from "react-icons/fi";

import "./ServiceDetail.css";

/* ─────────────────────────────────────────────
   CONFIG
───────────────────────────────────────────── */
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

/* ─────────────────────────────────────────────
   IMAGE URL HELPERS
───────────────────────────────────────────── */
function svcImg(filename) {
  if (!filename) return null;
  const bare = String(filename).split(/[\\/]/).pop();
  return `${API_BASE}/uploads/services/${bare}`;
}

function buildImages(service) {
  return ["image1", "image2", "image3", "image4", "image5"]
    .map((key) => service[key])
    .filter(Boolean)
    .map((filename) => ({ src: svcImg(filename), caption: service.title || "" }));
}

function shopImgSrc(filename) {
  if (!filename) return null;
  return `${API_BASE}/uploads/shop/${filename}`;
}

function fmtPrice(n) {
  return "R " + Number(n).toLocaleString("en-ZA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/* ─────────────────────────────────────────────
   STRIP HTML TAGS
───────────────────────────────────────────── */
function stripHtml(html) {
  if (!html) return "";
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s{2,}/g, " ")
    .trim();
}

/* ─────────────────────────────────────────────
   PARSE DESCRIPTION HTML
───────────────────────────────────────────── */
function parseDescription(html) {
  if (!html) return { prose: null, ulItems: [], olItems: [], sections: [] };

  const extractItems = (block) => {
    const items = [];
    const re = /<li[^>]*>([\s\S]*?)<\/li>/gi;
    let m;
    while ((m = re.exec(block)) !== null) {
      const text = stripHtml(m[1]).trim();
      if (text) items.push(text);
    }
    return items;
  };

  let ulItems = [];
  let olItems = [];

  const listRe = /<(ul|ol)[^>]*>([\s\S]*?)<\/\1>/gi;
  let match;
  while ((match = listRe.exec(html)) !== null) {
    const tag = match[1].toLowerCase();
    const items = extractItems(match[2]);
    if (tag === "ul") ulItems = ulItems.concat(items);
    else olItems = olItems.concat(items);
  }

  const withoutLists = html.replace(/<(ul|ol)[\s\S]*?<\/\1>/gi, "");
  const divTexts = [];
  const divRe = /<div[^>]*>([\s\S]*?)<\/div>/gi;
  let dm;
  while ((dm = divRe.exec(withoutLists)) !== null) {
    const t = stripHtml(dm[1]).trim();
    if (t.length > 30) divTexts.push(t);
  }

  const sections = divTexts.map((text) => ({ heading: null, body: text }));
  const plainAll = stripHtml(withoutLists).trim() || null;

  return { prose: plainAll, ulItems, olItems, sections };
}

/* ─────────────────────────────────────────────
   NO RIGHT-CLICK
───────────────────────────────────────────── */
function useNoContextMenu() {
  useEffect(() => {
    const h = (e) => e.preventDefault();
    document.addEventListener("contextmenu", h);
    return () => document.removeEventListener("contextmenu", h);
  }, []);
}

const noSaveProps = {
  draggable: false,
  onDragStart: (e) => e.preventDefault(),
  onContextMenu: (e) => e.preventDefault(),
};

/* ─────────────────────────────────────────────
   SHOP SLIDESHOW (right panel)
───────────────────────────────────────────── */
function ShopSlideshow({ products }) {
  const [idx, setIdx] = useState(0);
  const timerRef = useRef(null);

  const go = useCallback(
    (dir) => setIdx((prev) => (prev + dir + products.length) % products.length),
    [products.length]
  );

  useEffect(() => {
    if (products.length < 2) return;
    timerRef.current = setInterval(() => go(1), 4000);
    return () => clearInterval(timerRef.current);
  }, [go, products.length]);

  if (!products.length)
    return (
      <div className="sd-shop__slideshow sd-shop__slideshow--empty">
        <FiPackage size={36} />
        <p>No products available</p>
      </div>
    );

  const p = products[idx];
  const price = Number(p.sale_price || p.price);
  const oldPrice = p.sale_price ? Number(p.price) : null;
  const imgSrc = shopImgSrc(p.image1);

  return (
    <div className="sd-shop__slideshow">
      <div className="sd-shop__img-wrap">
        {imgSrc ? (
          <img src={imgSrc} alt={p.name} className="sd-shop__img" {...noSaveProps} />
        ) : (
          <div className="sd-shop__no-img"><FiPackage size={40} /></div>
        )}
        {p.discount_percent > 0 && (
          <span className="sd-shop__badge">-{Math.round(p.discount_percent)}%</span>
        )}
        {products.length > 1 && (
          <>
            <button className="sd-shop__nav sd-shop__nav--prev" onClick={() => go(-1)} aria-label="Previous">
              <FiChevronLeft size={15} />
            </button>
            <button className="sd-shop__nav sd-shop__nav--next" onClick={() => go(1)} aria-label="Next">
              <FiChevronRight size={15} />
            </button>
          </>
        )}
      </div>

      <div className="sd-shop__body">
        <p className="sd-shop__cat">{p.category_name || "Auto Part"}</p>
        <h4 className="sd-shop__name">{p.name}</h4>
        <div className="sd-shop__prices">
          <span className="sd-shop__price">{fmtPrice(price)}</span>
          {oldPrice && <span className="sd-shop__old">{fmtPrice(oldPrice)}</span>}
        </div>
        {p.car_make && (
          <p className="sd-shop__fitment">
            Fits: {[p.car_make, p.car_model].filter(Boolean).join(" ")}
            {p.car_year_from ? ` (${p.car_year_from}${p.car_year_to ? `–${p.car_year_to}` : "+"})` : ""}
          </p>
        )}
        <Link to={`/shop/${p.slug}`} className="sd-shop__btn">
          <FiShoppingCart size={13} /> View Product
        </Link>
      </div>

      {products.length > 1 && (
        <div className="sd-shop__dots">
          {products.map((_, i) => (
            <button
              key={i}
              className={`sd-shop__dot${i === idx ? " active" : ""}`}
              onClick={() => setIdx(i)}
              aria-label={`Product ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────
   LIGHTBOX
───────────────────────────────────────────── */
function Lightbox({ images, activeIdx, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose, onPrev, onNext]);

  if (activeIdx === null || !images[activeIdx]) return null;

  return (
    <div className="sd-lightbox" onClick={onClose}>
      <button className="sd-lightbox__close" onClick={onClose}><FiX size={22} /></button>
      <button
        className="sd-lightbox__nav sd-lightbox__nav--prev"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
      >
        <FiChevronLeft size={28} />
      </button>
      <div className="sd-lightbox__stage" onClick={(e) => e.stopPropagation()}>
        <img src={images[activeIdx].src} alt={images[activeIdx].caption} {...noSaveProps} />
        <div className="sd-lightbox__caption">
          <span className="sd-lightbox__count">{activeIdx + 1} / {images.length}</span>
          {images[activeIdx].caption}
        </div>
      </div>
      <button
        className="sd-lightbox__nav sd-lightbox__nav--next"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
      >
        <FiChevronRight size={28} />
      </button>
    </div>
  );
}

/* ─────────────────────────────────────────────
   SKELETON
───────────────────────────────────────────── */
function ServiceDetailSkeleton() {
  return (
    <main className="sd">
      <section style={{ minHeight: 460, background: "#1e1e1f", position: "relative" }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(90deg,#2a2a2b 25%,#333 50%,#2a2a2b 75%)",
          backgroundSize: "200% 100%",
          animation: "sdShimmer 1.4s infinite",
        }} />
      </section>
    </main>
  );
}

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function ServiceDetail() {
  useNoContextMenu();
  const { slug } = useParams();
  const navigate = useNavigate();

  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [contact, setContact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  const [shopProducts, setShopProducts] = useState([]);
  const [randomShop, setRandomShop] = useState([]);

  /* ── Fetch all services ── */
  useEffect(() => {
    fetch(`${API_BASE}/api/services`)
      .then((r) => r.ok ? r.json() : [])
      .then((data) => setAllServices(Array.isArray(data) ? data.filter((s) => s.active) : []))
      .catch(() => setAllServices([]));
  }, []);

  /* ── Fetch company contact ── */
  useEffect(() => {
    fetch(`${API_BASE}/api/company-contact`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => setContact(data))
      .catch(() => setContact(null));
  }, []);

  /* ── Fetch shop products for slideshow ── */
  useEffect(() => {
    fetch(`${API_BASE}/api/shop/products?limit=50&sort=newest`)
      .then((r) => r.json())
      .then((data) => {
        const prods = data.products || (Array.isArray(data) ? data : []);
        setShopProducts(prods);
      })
      .catch(() => {});
  }, []);

  /* ── Pick 10 random shop products ── */
  useEffect(() => {
    if (!shopProducts.length) return;
    const shuffled = [...shopProducts].sort(() => Math.random() - 0.5);
    setRandomShop(shuffled.slice(0, 10));
  }, [shopProducts]);

  /* ── Fetch service by slug ── */
  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    setNotFound(false);
    setService(null);
    window.scrollTo(0, 0);

    fetch(`${API_BASE}/api/services/${slug}`)
      .then((r) => { if (!r.ok) throw new Error("not found"); return r.json(); })
      .then((data) => { if (data && data.id) setService(data); else setNotFound(true); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  /* ── Lightbox helpers ── */
  const openLightbox = useCallback((i) => {
    setLightboxIdx(i);
    document.body.style.overflow = "hidden";
  }, []);

  const closeLightbox = useCallback(() => {
    setLightboxIdx(null);
    document.body.style.overflow = "";
  }, []);

  const prevLight = useCallback(() => {
    setLightboxIdx((i) => {
      const imgs = service ? buildImages(service) : [];
      return i <= 0 ? imgs.length - 1 : i - 1;
    });
  }, [service]);

  const nextLight = useCallback(() => {
    setLightboxIdx((i) => {
      const imgs = service ? buildImages(service) : [];
      return i >= imgs.length - 1 ? 0 : i + 1;
    });
  }, [service]);

  if (loading) return <ServiceDetailSkeleton />;

  if (notFound || !service) {
    return (
      <div className="sd-not-found">
        <h2>Service not found</h2>
        <Link to="/services" className="sd-back-btn">
          <FiArrowLeft size={16} /> Back to Services
        </Link>
      </div>
    );
  }

  /* ── Derived data ── */
  const images = buildImages(service);
  const hasImages = images.length > 0;
  const image1Src = images[0]?.src || null;
  const image2Src = images[1]?.src || null;
  const image3Src = images[2]?.src || null;
  const galleryImages = images;

  const { prose, ulItems, olItems, sections } = parseDescription(service.description);
  const heroBg = image1Src;

  const proseBlocks = sections.length > 0
    ? sections.map((s) => s.body)
    : prose ? [prose] : [];

  const mainProse = proseBlocks[0] || null;
  const benefitsProse = proseBlocks[1] || proseBlocks[0] || null;

  const currentIdx = allServices.findIndex((s) => s.slug === slug);

  return (
    <main className="sd">

      {/* ══════ 1. HERO ══════ */}
      <section className="sd-hero">
        <div
          className="sd-hero__bg"
          style={heroBg ? { backgroundImage: `url(${heroBg})` } : { background: "#1e1e1f" }}
        />
        <div className="sd-hero__overlay" />
        <div className="sd-hero__stripe" aria-hidden="true" />

        <div className="container sd-hero__body">
          <Link to="/services" className="sd-ani sd-ani--1 sd-hero__back">
            <FiArrowLeft size={14} /> All Services
          </Link>
          <div className="sd-ani sd-ani--2 sd-hero__label">OUR SERVICES</div>
          <h1 className="sd-ani sd-ani--3 sd-hero__title">{service.title}</h1>
        </div>

        <div className="sd-hero__crumb">
          <Link to="/">Home</Link>
          <FiArrowRight size={11} />
          <Link to="/services">Services</Link>
          <FiArrowRight size={11} />
          <span>{service.title}</span>
        </div>
      </section>

      {/* ══════ 2. MAIN BODY — sidebar | content | shop ══════ */}
      <section className="sd-body">
        <div className="container sd-body__inner">

          {/* ── LEFT SIDEBAR ── */}
          <aside className="sd-sidebar">
            <nav className="sd-sidebar__nav">
              {allServices.map((svc) => {
                const isActive = svc.slug === slug;
                return (
                  <Link
                    key={svc.id}
                    to={`/services/${svc.slug}`}
                    className={`sd-sidebar__nav-item ${isActive ? "sd-sidebar__nav-item--active" : ""}`}
                  >
                    <span>{svc.title}</span>
                    <span className="sd-sidebar__nav-arrow"><FiArrowRight size={14} /></span>
                  </Link>
                );
              })}
            </nav>

            {/* Call card */}
            <div className="sd-sidebar__call-card">
              {image2Src && (
                <div className="sd-sidebar__call-img">
                  <img src={image2Src} alt="Service" {...noSaveProps} />
                </div>
              )}
              <div className="sd-sidebar__call-body">
                <div className="sd-sidebar__call-icon"><FiPhone size={22} /></div>
                <p className="sd-sidebar__call-label">Call If You Need Help</p>
                <a
                  href={contact?.phone_number ? `tel:${contact.phone_number.replace(/\s/g, "")}` : "#"}
                  className="sd-sidebar__call-number"
                >
                  {contact?.phone_number || "—"}
                </a>
              </div>
            </div>
          </aside>

          {/* ── CENTER CONTENT ── */}
          <div className="sd-content">

            {/* Service title above image */}
            <h2 className="sd-content__h2 sd-content__h2--top">{service.title}</h2>

            {/* Main image */}
            {image1Src && (
              <div className="sd-content__hero-img" onClick={() => openLightbox(0)}>
                <img src={image1Src} alt={service.title} {...noSaveProps} />
                <div className="sd-content__hero-overlay" />
              </div>
            )}

            {/* Section 1 — main prose */}
            {mainProse && (
              <div className="sd-content__section">
                <p className="sd-content__prose">{mainProse}</p>
              </div>
            )}

            {/* Section 2 — Service Benefits */}
            {(olItems.length > 0 || ulItems.length > 0 || benefitsProse || image3Src) && (
              <div className="sd-content__section">
                <h2 className="sd-content__h2">Service Benefits</h2>

                <div className="sd-content__benefits-grid">
                  <div className="sd-content__benefits-left">
                    {benefitsProse && benefitsProse !== mainProse && (
                      <p className="sd-content__prose">{benefitsProse}</p>
                    )}
                    {olItems.length > 0 && (
                      <ul className="sd-content__checklist">
                        {olItems.map((item, i) => (
                          <li key={i} className="sd-content__checklist-item">
                            <FiCheckCircle size={15} />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {image3Src && (
                    <div className="sd-content__benefits-img" onClick={() => openLightbox(2)}>
                      <img src={image3Src} alt={service.title} {...noSaveProps} />
                      <div className="sd-content__benefits-img-overlay" />
                    </div>
                  )}
                </div>

                {ulItems.length > 0 && (
                  <div className="sd-content__ul-section">
                    {ulItems.map((item, i) => (
                      <div key={i} className="sd-content__ul-item">
                        <div className="sd-content__ul-num">{String(i + 1).padStart(2, "0")}</div>
                        <p>{item}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── RIGHT: Shop slideshow ── */}
          <aside className="sd-shop">
            <div className="sd-shop__inner">
              <ShopSlideshow products={randomShop} />
              <Link to="/shop" className="sd-shop__cta">
                Shop All Products <FiArrowRight size={13} />
              </Link>
            </div>
          </aside>

        </div>
      </section>

      {/* ══════ 3. GALLERY STRIP ══════ */}
      {galleryImages.length > 0 && (
        <section className="sd-gallery">
          <div className="container">
            <div className="sd-gallery__head">
              <span className="sd-gallery__tag">PROJECT GALLERY</span>
              <h3 className="sd-gallery__h3">View Our Work</h3>
            </div>
            <div className="sd-gallery__grid">
              {galleryImages.map((img, i) => (
                <button
                  key={i}
                  className="sd-gallery__item"
                  style={{ animationDelay: `${i * 0.07}s` }}
                  onClick={() => openLightbox(i)}
                >
                  <img src={img.src} alt={img.caption} {...noSaveProps} />
                  <div className="sd-gallery__item-overlay">
                    <span className="sd-gallery__item-caption">{img.caption}</span>
                    <span className="sd-gallery__item-icon">⤡</span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════ 4. PREV / NEXT ══════ */}
      <nav className="sd-service-nav">
        <div className="container sd-service-nav__inner">
          {currentIdx > 0 ? (
            <Link
              to={`/services/${allServices[currentIdx - 1].slug}`}
              className="sd-service-nav__btn sd-service-nav__btn--prev"
            >
              <FiChevronLeft size={20} />
              <span>
                <small>Previous Service</small>
                {allServices[currentIdx - 1].title}
              </span>
            </Link>
          ) : <div />}

          <Link to="/services" className="sd-service-nav__all">All Services</Link>

          {currentIdx < allServices.length - 1 ? (
            <Link
              to={`/services/${allServices[currentIdx + 1].slug}`}
              className="sd-service-nav__btn sd-service-nav__btn--next"
            >
              <span>
                <small>Next Service</small>
                {allServices[currentIdx + 1].title}
              </span>
              <FiChevronRight size={20} />
            </Link>
          ) : <div />}
        </div>
      </nav>

      {/* ══════ 5. CTA BAND ══════ */}
      <section className="sd-cta">
        <div className="container sd-cta__inner">
          <div className="sd-cta__text">
            <h3>Ready to Buy it</h3>
            <p>Go ahead and place your order here.</p>
          </div>
          <Link to="/shop" className="sd-cta__btn">
            Place your Order <FiArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ══════ LIGHTBOX ══════ */}
      {hasImages && (
        <Lightbox
          images={images}
          activeIdx={lightboxIdx}
          onClose={closeLightbox}
          onPrev={prevLight}
          onNext={nextLight}
        />
      )}
    </main>
  );
}